import { Grid, makeStyles } from '@material-ui/core';
import { AccessAlarm } from '@material-ui/icons';
import DatePicker from 'components/DatePicker';
import ToggleCard from 'components/ToggleCard';
import i18n from 'i18n';
import React from 'react';
import PersonAddDisabledIcon from '@material-ui/icons/PersonAddDisabled';
import clsx from 'clsx';
import {
  CompanyData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import { inAdminView, inSuperAdminView, inOperatorView } from 'utils/role';
import { format } from 'date-fns';
import styles from '../styles';

const useStyles = makeStyles(styles);

export interface BlockInstancesFormDisplay {
  enabled: boolean;
  auto_block_time: string;
}

export const blockInstancesInitialValues = {
  enabled: false,
  auto_block_time: '',
};

export interface Props {
  defaultValue: BlockInstancesFormDisplay | null;
  companySelected: CompanyData | null;
  role?: RolesEnum;
  permissions: PermissionsData | undefined;
  onChange: (values: BlockInstancesFormDisplay) => void;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
}

const BlockInstancesForm = ({
  defaultValue,
  role,
  companySelected,
  permissions,
  onChange,
  setHasError,
}: Props) => {
  const classes = useStyles();
  const [values, setValues] = React.useState<BlockInstancesFormDisplay>(
    blockInstancesInitialValues
  );

  const handleChange = (
    newValues: BlockInstancesFormDisplay,
    toggle?: boolean
  ) => {
    const updated =
      typeof toggle === 'boolean'
        ? { ...newValues, enabled: toggle }
        : newValues;
    setValues(updated);

    const payload: BlockInstancesFormDisplay = { ...updated };

    onChange(payload);
  };

  React.useEffect(() => {
    if (defaultValue) {
      setValues(defaultValue);
    }
  }, [defaultValue]);

  React.useEffect(() => {
    setHasError(
      !!values?.enabled && values?.auto_block_time === 'Invalid Date'
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  return (
    <ToggleCard
      active={values.enabled}
      id="toggle-auto-block"
      title={i18n.t('AUTO_BLOCK_INSTANCES_KEY')}
      getActive={(value: boolean) => handleChange(values, !value)}
      icon={
        <PersonAddDisabledIcon
          className={clsx({
            [classes.iconGreen]: inAdminView(role, companySelected),
            [classes.iconGray]: inSuperAdminView(role, companySelected),
            [classes.iconBlue]: inOperatorView(role, companySelected),
            [classes.iconYellow]: companySelected,
          })}
        />
      }
      disable={
        !!permissions && !permissions?.edit_env_auto_block_instances_maintenance
      }
    >
      <Grid container className={classes.FormContainer}>
        <AccessAlarm className={classes.iconMaintenance} />
        <Grid item className={classes.datePicker}>
          <DatePicker
            disabled={
              !!permissions &&
              !permissions?.edit_env_auto_block_instances_maintenance
            }
            id="run-time-auto-block-time"
            type="time"
            label={i18n.t('AUTO_BLOCK_INSTANCES_TIME_KEY')}
            value={
              values?.auto_block_time
                ? `Wed Nov 22 2023 ${values?.auto_block_time}`
                : null
            }
            error={values?.auto_block_time === 'Invalid Date'}
            keyValue="auto_block_time"
            setFieldsValue={(key, value) => {
              if (
                typeof value === 'object' &&
                String(value) !== 'Invalid Date' &&
                value !== null
              ) {
                const date = new Date(String(value));

                const formattedDate = format(date, 'HH:mm:ss');
                handleChange(
                  { ...values, [key]: formattedDate },
                  values.enabled
                );
              }
              if (String(value) === 'Invalid Date' || value === null) {
                handleChange(
                  { ...values, [key]: 'Invalid Date' },
                  values.enabled
                );
              }
            }}
          />
        </Grid>
      </Grid>
    </ToggleCard>
  );
};

export default BlockInstancesForm;
